/**
 * Game Comparison Calculations Utility
 * 
 * Provides client-side calculations for comparing pull tab games.
 * These functions calculate various metrics for game analysis including
 * expected value, win probabilities, efficiency scores, and rankings.
 * 
 * @module TabPicker.ComparisonCalculations
 */

import { GameListResponseGamesItem } from '../api/generated/schemas.ts/gameListResponseGamesItem';
import { GameListResponseGamesItemPayoutStructureTicketsItem } from '../api/generated/schemas.ts/gameListResponseGamesItemPayoutStructureTicketsItem';
import { ComparisonResults, GameComparison, ComparisonMetrics, GameDealStatus } from '../stores/comparisonStore';

/**
 * Calculate expected value for a game
 */
export function calculateExpectedValue(game: GameListResponseGamesItem): number {
  const ticketPrice = parseFloat(game.ticketPrice);
  const totalPayout = parseFloat(game.totalPayout);
  const totalTickets = game.totalTickets;
  
  // Expected value = (total payout / total tickets) - ticket price
  return (totalPayout / totalTickets) - ticketPrice;
}

/**
 * Calculate win probability (percentage of tickets that win any prize)
 */
export function calculateWinProbability(game: GameListResponseGamesItem): number {
  // Calculate total winning tickets from payout structure
  let totalWinningTickets = 0;
  
  if (game.payoutStructure?.tickets) {
    for (const tier of game.payoutStructure.tickets) {
      totalWinningTickets += (tier as GameListResponseGamesItemPayoutStructureTicketsItem).quantity;
    }
  }
  
  return totalWinningTickets / game.totalTickets;
}

/**
 * Calculate hold percentage (house edge)
 */
export function calculateHoldPercentage(game: GameListResponseGamesItem): number {
  const ticketPrice = parseFloat(game.ticketPrice);
  const totalPayout = parseFloat(game.totalPayout);
  const totalRevenue = ticketPrice * game.totalTickets;
  
  // Hold percentage = (total revenue - total payout) / total revenue * 100
  return ((totalRevenue - totalPayout) / totalRevenue) * 100;
}

/**
 * Calculate efficiency score (0-100, higher is better for players)
 */
export function calculateEfficiency(game: GameListResponseGamesItem): number {
  const expectedValue = calculateExpectedValue(game);
  const winProbability = calculateWinProbability(game);
  const ticketPrice = parseFloat(game.ticketPrice);
  
  // Efficiency combines expected value and win probability
  // Normalize to 0-100 scale where 100 is most efficient for players
  const expectedValueScore = Math.max(0, (expectedValue + ticketPrice) / ticketPrice * 50);
  const winProbabilityScore = winProbability * 50;
  
  return Math.min(100, expectedValueScore + winProbabilityScore);
}

/**
 * Calculate risk score (0-100, higher means riskier)
 */
export function calculateRiskScore(game: GameListResponseGamesItem): number {
  const winProbability = calculateWinProbability(game);
  const expectedValue = calculateExpectedValue(game);
  const ticketPrice = parseFloat(game.ticketPrice);
  
  // Risk is inverse of win probability plus expected value considerations
  const winRisk = (1 - winProbability) * 60; // 60% weight on win probability
  const valueRisk = expectedValue < 0 ? Math.abs(expectedValue) / ticketPrice * 40 : 0; // 40% weight on negative expected value
  
  return Math.min(100, winRisk + valueRisk);
}

/**
 * Calculate maximum prize from payout structure
 */
export function calculateMaxPrize(game: GameListResponseGamesItem): number {
  if (!game.payoutStructure?.tickets || game.payoutStructure.tickets.length === 0) {
    return 0;
  }
  
  return Math.max(...game.payoutStructure.tickets.map(tier => parseFloat((tier as GameListResponseGamesItemPayoutStructureTicketsItem).amount)));
}

/**
 * Calculate average prize when winning
 */
export function calculateAveragePrize(game: GameListResponseGamesItem): number {
  if (!game.payoutStructure?.tickets || game.payoutStructure.tickets.length === 0) {
    return 0;
  }
  
  let totalPrizeValue = 0;
  let totalWinningTickets = 0;
  
  for (const tier of game.payoutStructure.tickets) {
    const typedTier = tier as GameListResponseGamesItemPayoutStructureTicketsItem;
    const prizeValue = parseFloat(typedTier.amount);
    totalPrizeValue += prizeValue * typedTier.quantity;
    totalWinningTickets += typedTier.quantity;
  }
  
  return totalWinningTickets > 0 ? totalPrizeValue / totalWinningTickets : 0;
}

/**
 * Calculate all comparison metrics for a game
 */
export function calculateGameMetrics(game: GameListResponseGamesItem): Omit<ComparisonMetrics, 'expectedValueRank' | 'winProbabilityRank' | 'holdPercentageRank' | 'efficiencyRank' | 'riskScoreRank'> {
  return {
    expectedValue: calculateExpectedValue(game),
    winProbability: calculateWinProbability(game),
    holdPercentage: calculateHoldPercentage(game),
    efficiency: calculateEfficiency(game),
    riskScore: calculateRiskScore(game),
    maxPrize: calculateMaxPrize(game),
    averagePrize: calculateAveragePrize(game),
  };
}

/**
 * Rank games by a specific metric
 */
export function rankGamesByMetric<T>(
  games: T[],
  getValue: (game: T) => number,
  descending: boolean = true
): Array<T & { rank: number }> {
  // Sort games by the metric value
  const sorted = [...games].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);
    return descending ? bValue - aValue : aValue - bValue;
  });
  
  // Assign ranks (handle ties)
  const ranked = sorted.map((game, index) => ({
    ...game,
    rank: index + 1,
  }));
  
  return ranked;
}

/**
 * Calculate jar fill impact for a game at specific fill percentages
 */
export interface JarFillImpact {
  fillPercentage: number;
  adjustedExpectedValue: number;
  adjustedWinProbability: number;
  impactDescription: string;
}

export function calculateJarFillImpacts(
  game: GameListResponseGamesItem,
  fillPercentages: number[] = [25, 50, 75]
): JarFillImpact[] {
  const impacts: JarFillImpact[] = [];
  const originalExpectedValue = calculateExpectedValue(game);
  const originalWinProbability = calculateWinProbability(game);
  
  for (const fillPct of fillPercentages) {
    // Calculate remaining ratio (what's left in the jar)
    const remainingRatio = (100 - fillPct) / 100;
    
    // Create adjusted payout structure - proportionally reduce all prizes
    const adjustedPayoutStructure = {
      tickets: game.payoutStructure?.tickets?.map(tier => ({
        amount: (tier as GameListResponseGamesItemPayoutStructureTicketsItem).amount,
        quantity: Math.floor((tier as GameListResponseGamesItemPayoutStructureTicketsItem).quantity * remainingRatio)
      })) || []
    };
    
    // Create a game-like object with adjusted structure for calculations
    const adjustedGame = {
      ...game,
      payoutStructure: adjustedPayoutStructure,
      // Adjust total tickets proportionally as well
      totalTickets: Math.floor(game.totalTickets * remainingRatio)
    };
    
    // Calculate new metrics with adjusted structure
    const adjustedExpectedValue = calculateExpectedValue(adjustedGame);
    const adjustedWinProbability = calculateWinProbability(adjustedGame);
    
    // Generate impact description
    const valueDifference = adjustedExpectedValue - originalExpectedValue;
    const impactDescription = generateJarFillImpactDescription(
      fillPct, 
      valueDifference, 
      originalExpectedValue,
      adjustedWinProbability - originalWinProbability
    );
    
    impacts.push({
      fillPercentage: fillPct,
      adjustedExpectedValue,
      adjustedWinProbability,
      impactDescription
    });
  }
  
  return impacts;
}

/**
 * Generate human-readable impact description
 */
function generateJarFillImpactDescription(
  fillPercentage: number,
  valueDifference: number,
  originalExpectedValue: number,
  winProbabilityDifference: number
): string {
  const absValueDiff = Math.abs(valueDifference);
  const direction = valueDifference >= 0 ? 'increases' : 'reduces';
  
  if (absValueDiff < 0.01) {
    return `${fillPercentage}% claimed has minimal impact on expected value`;
  }
  
  const percentChange = Math.abs((valueDifference / originalExpectedValue) * 100);
  
  if (percentChange > 50) {
    return `${fillPercentage}% claimed ${direction} expected value significantly by $${absValueDiff.toFixed(3)}`;
  } else if (percentChange > 20) {
    return `${fillPercentage}% claimed ${direction} expected value moderately by $${absValueDiff.toFixed(3)}`;
  } else {
    return `${fillPercentage}% claimed ${direction} expected value by $${absValueDiff.toFixed(3)}`;
  }
}

/**
 * Deal status configuration for comparison
 */
export interface ComparisonConfig {
  dealStatuses?: GameDealStatus[];
  showOriginalOdds?: boolean;
}

/**
 * Apply deal status adjustments to a game
 * Accounts for both jar fill percentage and individually claimed prizes
 */
function applyDealStatusAdjustments(game: GameListResponseGamesItem, dealStatus?: GameDealStatus): GameListResponseGamesItem {
  if (!dealStatus) {
    return game;
  }

  const fillFactor = dealStatus.jarFillPercentage / 100;
  
  // First apply jar fill percentage adjustment to get base quantities
  const baseAdjustedTickets = game.payoutStructure?.tickets?.map(tier => ({
    amount: tier.amount,
    quantity: Math.floor((tier as GameListResponseGamesItemPayoutStructureTicketsItem).quantity * fillFactor),
  })) || [];

  // Then apply individual claimed prizes on top of jar fill adjustment
  const finalAdjustedTickets = baseAdjustedTickets.map(tier => {
    const claimedPrizesForTier = dealStatus.claimedPrizes?.[tier.amount];
    
    if (claimedPrizesForTier) {
      // Count how many individual prizes have been claimed for this tier
      const claimedCount = claimedPrizesForTier.filter(Boolean).length;
      // Subtract claimed prizes from the jar-fill-adjusted quantity
      const remainingQuantity = Math.max(0, tier.quantity - claimedCount);
      
      return {
        ...tier,
        quantity: remainingQuantity,
      };
    }
    
    return tier;
  });

  // Create adjusted game with both jar fill and claimed prize reductions
  const adjustedGame: GameListResponseGamesItem = {
    ...game,
    totalTickets: Math.floor(game.totalTickets * fillFactor),
    payoutStructure: {
      ...game.payoutStructure,
      tickets: finalAdjustedTickets,
    },
  };

  // Calculate total winning tickets after all adjustments
  const totalWinningTickets = finalAdjustedTickets.reduce((sum, tier) => sum + tier.quantity, 0);
  
  // Adjust total tickets to account for claimed winning tickets
  // If we've claimed winning tickets, reduce the total ticket count proportionally
  const originalWinningTickets = game.payoutStructure?.tickets?.reduce(
    (sum, tier) => sum + (tier as GameListResponseGamesItemPayoutStructureTicketsItem).quantity, 
    0
  ) || 0;
  
  if (originalWinningTickets > 0) {
    const winningTicketAdjustmentFactor = totalWinningTickets / originalWinningTickets;
    adjustedGame.totalTickets = Math.floor(game.totalTickets * fillFactor * winningTicketAdjustmentFactor);
  }

  // Recalculate total payout based on adjusted structure
  const adjustedTotalPayout = finalAdjustedTickets.reduce(
    (sum, tier) => sum + (parseFloat(tier.amount) * tier.quantity),
    0
  );
  
  adjustedGame.totalPayout = adjustedTotalPayout.toString();

  return adjustedGame;
}

/**
 * Perform full game comparison analysis
 */
export function performGameComparison(games: GameListResponseGamesItem[], config?: ComparisonConfig): ComparisonResults {
  if (games.length < 2) {
    throw new Error('At least 2 games are required for comparison');
  }
  
  // Apply deal status adjustments first
  const adjustedGames = games.map(game => {
    const dealStatus = config?.dealStatuses?.find(ds => ds.gameId === game.id);
    return applyDealStatusAdjustments(game, dealStatus);
  });
  
  // Calculate basic metrics for each adjusted game
  const gamesWithMetrics = adjustedGames.map(game => {
    const metrics = calculateGameMetrics(game);
    return {
      ...game,
      metrics,
    };
  });
  
  // Calculate rankings for each metric
  const expectedValueRanked = rankGamesByMetric(gamesWithMetrics, g => g.metrics.expectedValue, true);
  const winProbabilityRanked = rankGamesByMetric(gamesWithMetrics, g => g.metrics.winProbability, true);
  const holdPercentageRanked = rankGamesByMetric(gamesWithMetrics, g => g.metrics.holdPercentage, false); // Lower is better
  const efficiencyRanked = rankGamesByMetric(gamesWithMetrics, g => g.metrics.efficiency, true);
  const riskScoreRanked = rankGamesByMetric(gamesWithMetrics, g => g.metrics.riskScore, false); // Lower is better
  
  // Create final comparison results using adjusted games
  const comparisonGames: GameComparison[] = adjustedGames.map(game => {
    const gameMetrics = gamesWithMetrics.find(g => g.id === game.id)!.metrics;
    
    // Find rankings for this game
    const expectedValueRank = expectedValueRanked.find(g => g.id === game.id)!.rank;
    const winProbabilityRank = winProbabilityRanked.find(g => g.id === game.id)!.rank;
    const holdPercentageRank = holdPercentageRanked.find(g => g.id === game.id)!.rank;
    const efficiencyRank = efficiencyRanked.find(g => g.id === game.id)!.rank;
    const riskScoreRank = riskScoreRanked.find(g => g.id === game.id)!.rank;
    
    // Calculate jar fill impacts for Phase 1A display
    const jarFillImpacts = calculateJarFillImpacts(game);
    
    const comparisonMetrics: ComparisonMetrics = {
      ...gameMetrics,
      expectedValueRank,
      winProbabilityRank,
      holdPercentageRank,
      efficiencyRank,
      riskScoreRank,
      jarFillImpacts, // Add jar fill impact data
    };
    
    return {
      id: game.id,
      userId: game.userId,
      name: game.name,
      totalTickets: game.totalTickets,
      ticketPrice: game.ticketPrice,
      totalPayout: game.totalPayout,
      holdPercentage: game.holdPercentage || calculateHoldPercentage(game).toString(),
      payoutStructure: game.payoutStructure!,
      isActive: game.isActive,
      createdAt: game.createdAt,
      updatedAt: game.updatedAt,
      statistics: {
        expectedValue: gameMetrics.expectedValue,
        winProbability: gameMetrics.winProbability,
        averagePrize: gameMetrics.averagePrize,
        maxPrize: gameMetrics.maxPrize,
        profitMargin: -gameMetrics.expectedValue, // Negative expected value is house profit margin
      },
      comparisonMetrics,
    };
  });
  
  // Calculate summary
  const bestOverall = efficiencyRanked[0].id; // Best efficiency is overall best
  const highestExpectedValue = expectedValueRanked[0].id;
  const highestWinProbability = winProbabilityRanked[0].id;
  const lowestHoldPercentage = holdPercentageRanked[0].id;
  
  return {
    games: comparisonGames,
    summary: {
      totalGames: games.length,
      bestOverall,
      highestExpectedValue,
      highestWinProbability,
      lowestHoldPercentage,
      comparisonTimestamp: new Date().toISOString(),
    },
  };
}